// Smart extraction of Stream\Model\Base
// Combines the essential traits: Schema, Get, Set, Find, Count, Definitions
// Provides the same public API that all 110+ app models use.
// Queries local SQLite instead of MySQL.

import DB from 'offline/db'

// Schema type constants (from stream/model/constants/schema.php)
var SCHEMA_KEY = 1
var SCHEMA_NUMBER = 2
var SCHEMA_TEXT = 3
var SCHEMA_SLUG = 4
var SCHEMA_JSON = 5
var SCHEMA_RELATION = 6
var SCHEMA_FILE = 7
var SCHEMA_PASSWORD = 8
var SCHEMA_DATE = 9
var SCHEMA_DATE_UPDATE = 10
var SCHEMA_DATE_CREATE = 11
var SCHEMA_DATE_SAVE = 12
var SCHEMA_GALLERY = 13
var SCHEMA_URL = 14
var SCHEMA_COLLECTION = 15
var SCHEMA_MONEY = 16

function Model(data) {
	this._data = data || {}
	this._new = !data || !data[this.constructor._schema_key]
}

function escapeSql(value) {
	return String(value).replace(/'/g, "''")
}

function buildScalarClause(field, op, value) {
	op = String(op || '=').toLowerCase()

	if (Array.isArray(value)) {
		if (op !== 'in' && op !== 'not in' && op !== 'text in' && op !== 'text not in' && op !== 'contains one') {
			op = 'in'
		}

		if (op === 'text in' || op === 'text not in') {
			var textValues = []
			for (var ti = 0; ti < value.length; ti++) {
				var tv = escapeSql(String(value[ti]).toLowerCase())
				textValues.push("'" + tv + "'")
				textValues.push("'" + tv.replace(/-/g, ' ') + "'")
			}
			if (!textValues.length) return ''
			return 'LOWER(' + field + ') ' + (op === 'text in' ? 'IN' : 'NOT IN') + ' (' + textValues.join(',') + ')'
		}

		if (op === 'contains one') {
			var containsParts = []
			for (var ci = 0; ci < value.length; ci++) {
				containsParts.push(field + ' LIKE \'%"' + escapeSql(value[ci]) + '"%\'')
			}
			return containsParts.length ? '(' + containsParts.join(' OR ') + ')' : ''
		}

		if (op === 'contains' || op === 'not contains') {
			var arrayContainsOp = op === 'contains' ? 'LIKE' : 'NOT LIKE'
			return field + ' ' + arrayContainsOp + ' \'%"' + escapeSql(value[0]) + '"%\''
		}

		if (op === 'in' || op === 'not in') {
			var inValues = value.map(function(v) { return "'" + escapeSql(v) + "'" })
			if (!inValues.length) return ''
			return field + ' ' + (op === 'in' ? 'IN' : 'NOT IN') + ' (' + inValues.join(',') + ')'
		}
	}

	if (op === 'contains' || op === 'not contains') {
		var containsOp = op === 'contains' ? 'LIKE' : 'NOT LIKE'
		return field + ' ' + containsOp + ' \'%"' + escapeSql(value) + '"%\''
	}

	if (op === 'like' || op === 'not like') {
		return field + ' ' + op.toUpperCase() + ' \'' + escapeSql(value) + '\''
	}

	return field + ' ' + op + ' \'' + escapeSql(value) + '\''
}

function buildWhereParts(where) {
	if (!where) return []

	if (!Array.isArray(where) && typeof where === 'object') {
		var objectParts = []
		var keys = Object.keys(where)
		for (var i = 0; i < keys.length; i++) {
			objectParts.push(buildScalarClause(keys[i], Array.isArray(where[keys[i]]) ? 'in' : '=', where[keys[i]]))
		}
		return objectParts.filter(Boolean)
	}

	if (Array.isArray(where) && where.length === 3 && typeof where[0] === 'string') {
		return [buildScalarClause(where[0], where[1], where[2])].filter(Boolean)
	}

	if (Array.isArray(where)) {
		var clauseParts = []
		for (var wi = 0; wi < where.length; wi++) {
			if (Array.isArray(where[wi]) && where[wi].length === 3) {
				var built = buildScalarClause(where[wi][0], where[wi][1], where[wi][2])
				if (built) clauseParts.push(built)
			}
		}
		return clauseParts
	}

	return []
}

// ---- Static schema accessors ----

Model.schema_table = function() { return this._schema_table }
Model.schema_key = function() { return this._schema_key }
Model.schema_prefix = function() { return this._schema_prefix }
Model.schema_fields = function() { return Object.keys(this._schema_fields) }

Model.schema_field_types = function() { return this._schema_fields }

Model.schema_table_rename = function(name) {
	this._schema_table = name
}

Model.primary_key = function() { return this._schema_key }

// ---- Static finder methods ----

// Load single record by primary key
Model.with_id = function(id) {
	var rows = DB.query(
		'SELECT * FROM ' + this._schema_table + ' WHERE ' + this._schema_key + ' = ?', [id]
	)
	if (!rows.length) return null
	return new this(rows[0])
}

// Load single record by field
Model.with = function(field, value) {
	return this.record({ where: [field, '=', value] })
}

// Load single record by config
Model.record = function(config) {
	var sql = 'SELECT * FROM ' + this._schema_table

	if (config && config.where) {
		var whereStr = buildWhere(config.where)
		if (whereStr) sql += ' WHERE ' + whereStr
	}

	if (config && config.order_by) {
		sql += ' ORDER BY ' + config.order_by
	}

	sql += ' LIMIT 1'

	var rows = DB.query(sql)
	if (!rows.length) return null
	return new this(rows[0])
}

// Load collection of records, returns array of model instances
Model.collection = function(config) {
	var sql = 'SELECT * FROM ' + this._schema_table

	if (config && config.where) {
		var whereStr = buildWhere(config.where)
		if (whereStr) sql += ' WHERE ' + whereStr
	}

	if (config && config.order_by) {
		sql += ' ORDER BY ' + config.order_by
	}

	if (config && config.limit) {
		sql += ' LIMIT ' + config.limit
	}

	var self = this
	var rows = DB.queryFast(sql)
	var result = rows.map(function(row) { return new self(row) })

	// Add .associated() and .each() helpers to the result array
	result.associated = function(field) {
		var map = {}
		for (var ai = 0; ai < result.length; ai++) {
			var key = field ? result[ai]._getData(field) : result[ai].get_id()
			map[key] = result[ai]
		}
		return map
	}

	result.each = function(fn) {
		for (var ei = 0; ei < result.length; ei++) {
			fn(result[ei])
		}
		return result
	}

	return result
}

// Load collection indexed by a field
Model.indexed = function(index, config) {
	var sql = 'SELECT * FROM ' + this._schema_table

	if (config && config.where) {
		var whereStr = buildWhere(config.where)
		if (whereStr) sql += ' WHERE ' + whereStr
	}

	if (config && config.order_by) {
		sql += ' ORDER BY ' + config.order_by
	}

	var rows = DB.queryFast(sql)
	var result = {}

	if (index) {
		for (var i = 0; i < rows.length; i++) {
			var key = rows[i][index]
			result[key] = new this(rows[i])
		}
	} else {
		for (var j = 0; j < rows.length; j++) {
			result[rows[j][this._schema_key]] = new this(rows[j])
		}
	}

	return result
}

// Load records matching where clause
Model.where = function(config) {
	return this.collection({ where: config })
}

// Count records
Model.total = function(config) {
	var sql = 'SELECT COUNT(*) as cnt FROM ' + this._schema_table

	if (config && config.where) {
		var whereStr = buildWhere(config.where)
		if (whereStr) sql += ' WHERE ' + whereStr
	}

	var rows = DB.queryFast(sql)
	return rows.length ? rows[0].cnt : 0
}

// Advanced filter method (port of PHP Model::filter with arrayed/select/sum/min/group/indexed support)
Model.filter = function(config) {
	if (!config) return this.collection(config)

	var selectParts = []
	var hasCustomSelect = false

	// Build SELECT clause from config.select
	if (config.select) {
		hasCustomSelect = true

		if (config.select.plain) {
			for (var i = 0; i < config.select.plain.length; i++) {
				selectParts.push(config.select.plain[i])
			}
		}
		if (config.select.rename) {
			var rkeys = Object.keys(config.select.rename)
			for (var ri = 0; ri < rkeys.length; ri++) {
				selectParts.push(rkeys[ri] + ' AS ' + config.select.rename[rkeys[ri]])
			}
		}
		if (config.select.sum) {
			var skeys = Object.keys(config.select.sum)
			for (var si = 0; si < skeys.length; si++) {
				selectParts.push('SUM(' + skeys[si] + ') AS ' + config.select.sum[skeys[si]])
			}
		}
		if (config.select.min) {
			var mkeys = Object.keys(config.select.min)
			for (var mi = 0; mi < mkeys.length; mi++) {
				selectParts.push('MIN(' + mkeys[mi] + ') AS ' + config.select.min[mkeys[mi]])
			}
		}
		if (config.select.count) {
			var ckeys = Object.keys(config.select.count)
			for (var ci = 0; ci < ckeys.length; ci++) {
				selectParts.push('COUNT(' + ckeys[ci] + ') AS ' + config.select.count[ckeys[ci]])
			}
		}
	}

	var sql = 'SELECT ' + (hasCustomSelect ? selectParts.join(', ') : '*') + ' FROM ' + this._schema_table

	// WHERE
	if (config.where) {
		var whereParts = buildWhereParts(config.where)
		if (whereParts.length) sql += ' WHERE ' + whereParts.join(' AND ')
	}

	// GROUP BY
	if (config.group) {
		sql += ' GROUP BY ' + config.group.join(', ')
	}

	// HAVING
	if (config.having) {
		sql += ' HAVING ' + config.having
	}

	// ORDER BY
	if (config.order) {
		var orderParts = []
		var okeys = Object.keys(config.order)
		for (var oi = 0; oi < okeys.length; oi++) {
			orderParts.push(okeys[oi] + ' ' + config.order[okeys[oi]])
		}
		sql += ' ORDER BY ' + orderParts.join(', ')
	} else if (config.order_by) {
		sql += ' ORDER BY ' + config.order_by
	}

	// LIMIT
	if (config.limit) {
		sql += ' LIMIT ' + config.limit
	}

	var rows = DB.queryFast(sql)

	// Return raw arrays if arrayed
	if (config.arrayed) return rows

	// Return indexed by field
	if (config.indexed) {
		var indexed = {}
		for (var ii = 0; ii < rows.length; ii++) {
			indexed[rows[ii][config.indexed]] = rows[ii][config.indexed]
		}
		return indexed
	}

	// Default: return model instances
	var self = this
	return rows.map(function(row) { return new self(row) })
}

// Sum a field with optional where clause
Model.sum = function(field, config) {
	var sql = 'SELECT SUM(' + field + ') AS total FROM ' + this._schema_table

	if (config && config.where) {
		var whereParts = buildWhereParts(config.where)
		if (whereParts.length) sql += ' WHERE ' + whereParts.join(' AND ')
	}

	var rows = DB.queryFast(sql)
	return rows.length ? (rows[0].total || 0) : 0
}

// Total with custom aggregate expression
Model.total = function(config, extra, expr) {
	var countExpr = expr || 'COUNT(*)'
	var sql = 'SELECT ' + countExpr + ' AS cnt FROM ' + this._schema_table

	if (config && config.where) {
		var whereParts = buildWhereParts(config.where)
		if (whereParts.length) sql += ' WHERE ' + whereParts.join(' AND ')
	}

	var rows = DB.queryFast(sql)
	return rows.length ? (rows[0].cnt || 0) : 0
}

// Forge a new instance (read-only stub - just wraps data)
Model.forge = function(data) {
	return new this(data || {})
}

// ---- Instance methods ----

Model.prototype.get_id = function() {
	return this._data[this.constructor._schema_key]
}

Model.prototype.is_new = function() {
	return this._new
}

// Dynamic getter: get_fieldname() returns value, auto-decodes JSON
Model.prototype.get = function() {
	var field = this.constructor._schema_prefix + '_' + Array.prototype.join.call(arguments, '_')
	return this._getData(field)
}

Model.prototype._getData = function(field) {
	var val = this._data[field]
	if (val === undefined) return null

	// Auto-decode JSON fields
	var fieldType = this.constructor._schema_fields[field]
	if (fieldType === SCHEMA_JSON && typeof val === 'string') {
		try { return JSON.parse(val) } catch (e) { return val }
	}

	return val
}

// Batch set
Model.prototype.set = function(data) {
	var keys = Object.keys(data)
	for (var i = 0; i < keys.length; i++) {
		this._data[keys[i]] = data[keys[i]]
	}
	return this
}

Model.prototype.has = function(name) {
	var field = this.constructor._schema_prefix + '_' + name
	return this.constructor._schema_fields[field] !== undefined
}

Model.prototype.to_array = function() {
	return Object.assign({}, this._data)
}

Model.prototype.to_json = function() {
	return JSON.stringify(this.to_array())
}

Model.prototype.rawData = function() {
	return this._data
}

// Save stub (read-only for now)
Model.prototype.save = function() {
	console.warn('Model.save() called in offline mode - not implemented')
	return this
}

// Delete stub (read-only for now)
Model.prototype.delete = function() {
	console.warn('Model.delete() called in offline mode - not implemented')
}

// ---- Magic getter/setter generation ----
// Models call get_name(), get_id(), etc. We use a Proxy to handle this.
// For environments without Proxy, models should call this._getData(field) directly.

Model.prototype.__get = function(name) {
	var prefix = this.constructor._schema_prefix
	var field = prefix ? prefix + '_' + name : name
	var val = this._getData(field)
	// Fallback: if prefixed field not found, try raw name (supports both get_value() and get_rating_value())
	if (val === undefined && prefix && name.indexOf(prefix + '_') === 0) {
		val = this._getData(name)
	}
	return val
}

// ---- Helper: create a model class extending Base ----

Model.extend = function(schema) {
	function Child(data) {
		Model.call(this, data)
	}

	Child.prototype = Object.create(Model.prototype)
	Child.prototype.constructor = Child

	// Copy static methods
	var staticKeys = Object.keys(Model)
	for (var i = 0; i < staticKeys.length; i++) {
		if (typeof Model[staticKeys[i]] === 'function') {
			Child[staticKeys[i]] = Model[staticKeys[i]]
		}
	}

	// Apply schema
	Child._schema_table = schema.table
	Child._schema_prefix = schema.prefix || ''
	Child._schema_key = schema.key
	Child._schema_fields = schema.fields || {}
	Child._schema_default = schema.defaults || {}

	// Generate get_* methods for each field
	var fields = Object.keys(Child._schema_fields)
	for (var j = 0; j < fields.length; j++) {
		var fieldName = fields[j]
		var methodSuffix = fieldName

		// Strip prefix for method name: user_name -> get_name()
		if (Child._schema_prefix && fieldName.indexOf(Child._schema_prefix + '_') === 0) {
			methodSuffix = fieldName.substring(Child._schema_prefix.length + 1)
		}

		// Create getter (short name: get_name for user_name)
		(function(fn, ms) {
			Child.prototype['get_' + ms] = function() { return this._getData(fn) }
			Child.prototype['set_' + ms] = function(v) { this._data[fn] = v; return this }
			// Also create full-name getter if prefix was stripped (get_user_name for user_name)
			if (ms !== fn) {
				Child.prototype['get_' + fn] = function() { return this._getData(fn) }
			}
		})(fieldName, methodSuffix)
	}

	// Apply custom instance methods (don't override auto-generated getters unless explicit)
	if (schema.methods) {
		var methodKeys = Object.keys(schema.methods)
		for (var m = 0; m < methodKeys.length; m++) {
			Child.prototype[methodKeys[m]] = schema.methods[methodKeys[m]]
		}
	}

	// Apply custom static methods
	if (schema.statics) {
		var staticMethodKeys = Object.keys(schema.statics)
		for (var s = 0; s < staticMethodKeys.length; s++) {
			Child[staticMethodKeys[s]] = schema.statics[staticMethodKeys[s]]
		}
	}

	return Child
}

// ---- Internal SQL building ----

function buildWhere(where) {
	return buildWhereParts(where).join(' AND ')
}

// Export constants alongside the class
Model.SCHEMA_KEY = SCHEMA_KEY
Model.SCHEMA_NUMBER = SCHEMA_NUMBER
Model.SCHEMA_TEXT = SCHEMA_TEXT
Model.SCHEMA_SLUG = SCHEMA_SLUG
Model.SCHEMA_JSON = SCHEMA_JSON
Model.SCHEMA_RELATION = SCHEMA_RELATION
Model.SCHEMA_FILE = SCHEMA_FILE
Model.SCHEMA_PASSWORD = SCHEMA_PASSWORD
Model.SCHEMA_DATE = SCHEMA_DATE
Model.SCHEMA_DATE_UPDATE = SCHEMA_DATE_UPDATE
Model.SCHEMA_DATE_CREATE = SCHEMA_DATE_CREATE
Model.SCHEMA_DATE_SAVE = SCHEMA_DATE_SAVE
Model.SCHEMA_GALLERY = SCHEMA_GALLERY
Model.SCHEMA_URL = SCHEMA_URL
Model.SCHEMA_COLLECTION = SCHEMA_COLLECTION
Model.SCHEMA_MONEY = SCHEMA_MONEY

export default Model
